import React, { useState, useEffect } from 'react';
import {
  View,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  useWindowDimensions,
  Platform,
} from 'react-native';
import Text from '../../components/text';
import { useRoute } from '@react-navigation/native';
import ajax from '../../helpers/ajaxHelper';
import Urls from '../../services/Urls';
import drawerHoc from '../../components/drawerHOC/DrawerHOC';
import MarkAsComplete from '../../components/markAsComplete/MarkAsComplete';
import DropDownPicker from '../../components/picker/DropDownPicker';
import STRINGS from '../tenants/redux/strings';
import {
  AddressValidation,
  EmailValidation,
  LastValidation,
  NameValidation,
  PostCodeValidation,
} from '../../helpers/validation';
import { showErrorToast, showSuccesToast } from '../tenants/tenantOnboarding/helpers';
import { AntDesign, Ionicons } from '@expo/vector-icons';
import { SHADOWS, LAYOUT } from '../../constants/theme';
import { SetupSidebar, usePropertyRouteParams, PropertyBreadcrumb, PropertyHeaderBanner, PropertyStickyFooter } from './sharedComponents';

/* ─── Styled TextInput wrapper ────────────────────────────────── */
const FInput = ({ label, required, placeholder, value, onChangeText, editable = true, error, keyboardType }) => (
  <View style={fi.wrap}>
    {label ? (
      <Text style={fi.label}>
        {label}
        {required && <Text style={fi.star}> *</Text>}
      </Text>
    ) : null}
    <TextInput
      style={[fi.input, !editable && fi.inputDisabled, error && fi.inputError]}
      placeholder={placeholder || ''}
      placeholderTextColor="#b0b0b0"
      value={value || ''}
      onChangeText={onChangeText}
      editable={editable}
      keyboardType={keyboardType || 'default'}
    />
    {error ? <Text style={fi.errorText}>{error}</Text> : null}
  </View>
);

const fi = StyleSheet.create({
  wrap: { marginBottom: 16 },
  label: { fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 6 },
  star: { color: '#EF4444' },
  input: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
    fontSize: 14,
    color: '#111827',
    backgroundColor: '#fff',
  },
  inputDisabled: { backgroundColor: '#F9FAFB', color: '#9CA3AF' },
  inputError: { borderColor: '#EF4444' },
  errorText: { fontSize: 11, color: '#EF4444', marginTop: 4 },
});

/* ─── Yes / No toggle buttons ─────────────────────────────────── */
const YesNoToggle = ({ value, onChange }) => (
  <View style={yn.row}>
    <TouchableOpacity
      style={[yn.btn, value === 'Yes' && yn.btnActive]}
      onPress={() => onChange('Yes')}
      activeOpacity={0.8}
    >
      <Text style={[yn.btnText, value === 'Yes' && yn.btnTextActive]}>Yes</Text>
    </TouchableOpacity>
    <TouchableOpacity
      style={[yn.btn, value === 'No' && yn.btnActive]}
      onPress={() => onChange('No')}
      activeOpacity={0.8}
    >
      <Text style={[yn.btnText, value === 'No' && yn.btnTextActive]}>No</Text>
    </TouchableOpacity>
  </View>
);

const yn = StyleSheet.create({
  row: { flexDirection: 'row', gap: 8 },
  btn: {
    paddingHorizontal: 28,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: '#D1D5DB',
    backgroundColor: '#fff',
  },
  btnActive: { backgroundColor: '#6C2BD9', borderColor: '#6C2BD9' },
  btnText: { fontSize: 14, fontWeight: '600', color: '#374151' },
  btnTextActive: { color: '#fff' },
});



/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
const LandlordDetails = ({ navigation }) => {
  const routeParams = usePropertyRouteParams();
  const route = useRoute();
  const activePropertyId = routeParams.id;
  const propertyDisplayName = routeParams.name || 'Property';
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;

  /* ── State (unchanged from original) ── */
  const [state, setState] = useState({
    title: '', first_name: '', last_name: '',
    contact_number: '', email: '', address: '', postcode: '',
  });

  const [desLandlord, setDesLandlord] = useState({
    title: '', first_name: '', last_name: '',
    contact_number: '', email: '', address: '', postcode: '',
  });
  const [isDesignatedLandlord, setIsDesignatedLandlord] = useState('');
  const [titleError, setTitleError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [lastError, setLastError] = useState('');
  const [addressError, setAddressError] = useState('');
  const [pinCodeError, setPinCodeError] = useState('');
  const [firstNameError, setFirstNameError] = useState('');

  const _handleChange = (e) => {
    setDesLandlord({ ...desLandlord, [e.label]: e.value });
  };

  useEffect(() => {
    if (activePropertyId) {
      getPropertyDetails();
    }
  }, [activePropertyId]);

  const getPropertyDetails = () => {
    if (!activePropertyId) return;
    ajax
      .get(`${Urls.Properties}/${activePropertyId}`)
      .then(({ data }) => {
        if (data.status) {
          setIsDesignatedLandlord(data?.payload?.is_designated_landlord ? 'Yes' : 'No');
          setState(data?.payload?.landlord);
          setDesLandlord(data?.payload.designatedLandlord || desLandlord);
        }
      })
      .catch((error) => console.log('error', error));
  };

  const _validation = () => {
    const emailValidate = EmailValidation(desLandlord.email);
    const firstNameValidate = NameValidation(desLandlord.first_name);
    const lastNameValidate = LastValidation(desLandlord.last_name);
    const postCodeValidate = PostCodeValidation(desLandlord.postcode);
    const addressValidate = AddressValidation(desLandlord.address);
    const titleValidate = desLandlord.title
      ? { status: true }
      : { status: false, msg: '*Required' };

    if (
      titleValidate.status &&
      emailValidate.status &&
      firstNameValidate.status &&
      lastNameValidate.status &&
      addressValidate.status &&
      postCodeValidate.status
    ) {
      _onSave();
    } else {
      setTitleError(titleValidate?.status ? '' : titleValidate?.msg);
      setEmailError(emailValidate?.status ? '' : emailValidate?.msg);
      setAddressError(addressValidate?.status ? '' : addressValidate?.msg);
      setFirstNameError(firstNameValidate?.status ? '' : firstNameValidate?.msg);
      setLastError(lastNameValidate?.status ? '' : lastNameValidate?.msg);
      setPinCodeError(postCodeValidate?.status ? '' : postCodeValidate?.msg);
    }
  };

  const _onSave = () => {
    const requestBody = {
      is_designated_landlord: isDesignatedLandlord && isDesignatedLandlord === 'Yes',
      landlord_details: {
        title: isDesignatedLandlord === 'No' ? desLandlord?.title : state?.title,
        first_name: isDesignatedLandlord === 'No' ? desLandlord?.first_name : state?.first_name,
        last_name: isDesignatedLandlord === 'No' ? desLandlord?.last_name : state?.last_name,
        contact_number: isDesignatedLandlord === 'No' ? desLandlord?.contact_number : state?.contact_number,
        email: isDesignatedLandlord === 'No' ? desLandlord?.email : state?.email,
        address: isDesignatedLandlord === 'No' ? desLandlord?.address : state?.address,
        postcode: isDesignatedLandlord === 'No' ? desLandlord?.postcode : state?.postcode,
      },
    };
    ajax
      .post(`${Urls.LAND_DETAILS}/${activePropertyId}`, requestBody)
      .then(({ data }) => {
        if (data?.status) {
          showSuccesToast(data?.data);
          navigation.navigate('PropertySection', { id: activePropertyId, name: propertyDisplayName });
        }
      })
      .catch((error) => {
        console.log('error', error);
      });
  };
  const _onPostcodeSearch = () => {
    if (!state?.postcode) {
      // alert('Please enter postcode value');
      showErrorToast('Please enter postcode value');
    } else {
      ajax
        .post(Urls.SearchAddress, { postcode: state?.postcode })
        .then(({ data }) => {
          if (data.status) {
            try {
              // NOTE: new logic as per Andrew updated search postcodes API
              if (data?.data?.message) {
                alert(data?.data?.message);
                setPostcodes([]);
              }

              if (data?.data?.results || data?.data?.matches) {
                let temp = [];

                let items = data?.data?.matches || data?.data?.results;
                for (let key in items) {
                  let obj = items[key];
                  let label = getAddress(obj);
                  temp = [...temp, { label: label, value: JSON.stringify(obj) }];
                }

                setPostcodes(temp);
              }
            } catch (err) {
              setPostcodes([]);
            }
          } else {
            alert(data.message);
          }
        })
        .catch((error) => {
          console.log('error', error);
        });
    }
  };

  const isDisabled = isDesignatedLandlord === 'Yes';

  /* ── helpers ── */
  const fieldVal = (desKey, stateKey) =>
    isDesignatedLandlord === 'No' ? desLandlord?.[desKey] : state?.[stateKey];
  const fieldChange = (key) => (val) => _handleChange({ value: val, label: key });

  /* ════════════════════════════════════════
     MOBILE LAYOUT
  ════════════════════════════════════════ */
  if (isMobile) {
    return (
      <View style={ms.root}>
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={ms.scroll}
          showsVerticalScrollIndicator={false}
        >
          {/* Setup progress banner */}


          {/* Header card */}
          <View style={ms.headerCard}>
            <View style={ms.headerIcon}>
              <Ionicons name="person-outline" size={20} color="#6C2BD9" />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={ms.headerTitle}>Landlord Details</Text>
              <Text style={ms.headerSub}>Enter landlord profile details for this property</Text>
            </View>
          </View>

          {/* Mark as complete */}
          <View style={ms.completeCard}>
            <MarkAsComplete
              label={'Landlord details'}
              flowID={route?.params?.id}
              flowtype={route?.params?.key}
              isComplete={route?.params?.status === 2 ? true : false}
            />
          </View>

          {/* Are you the landlord? */}
          <View style={ms.sectionCard}>
            <Text style={ms.sectionTitle}>Are you the landlord?</Text>
            <Text style={ms.sectionSub}>Edit these fields to pre-fill your profile.</Text>
            <View style={{ marginTop: 14 }}>
              <YesNoToggle value={isDesignatedLandlord} onChange={setIsDesignatedLandlord} />
            </View>
          </View>

          {/* Conditional fields */}
          {isDesignatedLandlord ? (
            <View style={ms.sectionCard}>
              {isDesignatedLandlord === 'Yes' && (
                <View style={ms.infoNote}>
                  <Ionicons name="information-circle-outline" size={16} color="#6C2BD9" />
                  <Text style={ms.infoNoteText}>
                    You cannot edit items below. Go to your account{' '}
                    <Text
                      style={{ color: '#6C2BD9', fontWeight: '700' }}
                      onPress={() => navigation.navigate('AccountEdit')}
                    >
                      settings
                    </Text>{' '}
                    page to edit your details.
                  </Text>
                </View>
              )}

              {/* Title dropdown */}
              <View style={{ marginBottom: 16 }}>
                <DropDownPicker
                  disabled={isDisabled}
                  items={STRINGS.OPENFLOW_TITLES.map((ITEM) => ({ value: ITEM, label: ITEM }))}
                  title={'Title'}
                  defaultValue={isDesignatedLandlord === 'No' ? desLandlord?.title : state?.title}
                  onChangeItem={(e) => _handleChange({ value: e.value, label: 'title' })}
                  error={titleError ? true : false}
                />
              </View>

              <FInput
                label="First Name"
                required
                value={fieldVal('first_name', 'first_name')}
                onChangeText={fieldChange('first_name')}
                editable={!isDisabled}
                error={firstNameError}
              />
              <FInput
                label="Last Name"
                required
                value={fieldVal('last_name', 'last_name')}
                onChangeText={fieldChange('last_name')}
                editable={!isDisabled}
                error={lastError}
              />
              <FInput
                label="Current Address"
                placeholder="Enter your full address"
                value={fieldVal('address', 'address')}
                onChangeText={fieldChange('address')}
                editable={!isDisabled}
                error={addressError}
              />
              <FInput
                label="Postcode"
                value={fieldVal('postcode', 'postcode')}
                onChangeText={fieldChange('postcode')}
                editable={!isDisabled}
                error={pinCodeError}
              />

              <Text style={ms.contactsLabel}>CONTACTS</Text>

              <FInput
                label="Contact Number"
                placeholder="e.g. +44 20 7123 4567"
                value={fieldVal('contact_number', 'contact_number')}
                onChangeText={(val) => {
                  const numericValue = val.replace(/[^0-9]/g, '');
                  _handleChange({ value: numericValue, label: 'contact_number' });
                }}
                editable={!isDisabled}
                keyboardType="phone-pad"
              />
              <FInput
                label="Email Address"
                required
                placeholder="hubita@gmail.com"
                value={fieldVal('email', 'email')}
                onChangeText={fieldChange('email')}
                editable={!isDisabled}
                error={emailError}
                keyboardType="email-address"
              />
            </View>
          ) : null}

          <View style={{ height: 100 }} />
        </ScrollView>

        {/* Sticky footer CTA */}
        <PropertyStickyFooter
          onSave={() => {
            isDesignatedLandlord === 'No' ? _validation() : _onSave();
          }}
          isMobile={isMobile} />
      </View>
    );
  }

  /* ════════════════════════════════════════
     WEB / TABLET LAYOUT
  ════════════════════════════════════════ */
  return (
    <View style={{ flex: 1, position: 'relative', backgroundColor: '#F5F7FB' }}>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingBottom: 100 }}
        showsVerticalScrollIndicator={false}
      >
        {/* Breadcrumb */}
        <PropertyBreadcrumb
          navigation={navigation}
          propertyId={activePropertyId}
          propertyName={propertyDisplayName}
          currentStep="Landlord Details"
        />

        {/* Header Banner Card */}
        <PropertyHeaderBanner
          icon="person-outline"
          title="Landlord Details"
          subtitle="Please fill in the landlord details for this property."
          isMobile={isMobile}
          rightComponent={
            activePropertyId ? (
              <MarkAsComplete
                label={'Landlord details'}
                flowID={activePropertyId}
                flowtype={routeParams?.key}
                isComplete={routeParams?.status === 2}
              />
            ) : null
          }
        />

        {/* Body: main card + sidebar */}
        <View style={ws.body}>
          {/* ── Main Form Card ── */}
          <View style={ws.mainCard}>
            {/* Are you the landlord? */}
            <View style={ws.questionRow}>
              <View style={{ flex: 1 }}>
                <Text style={ws.questionTitle}>Are you the landlord?</Text>
                <Text style={ws.questionSub}>
                  You can edit all fields below to pre-fill your landlord profile.
                </Text>
              </View>
              <YesNoToggle value={isDesignatedLandlord} onChange={setIsDesignatedLandlord} />
            </View>

            {isDesignatedLandlord ? (
              <>
                {isDesignatedLandlord === 'Yes' && (
                  <View style={ws.infoNote}>
                    <Ionicons name="information-circle-outline" size={16} color="#6C2BD9" />
                    <Text style={ws.infoNoteText}>
                      You cannot edit any items below. Please go to your account{' '}
                      <Text
                        style={{ color: '#6C2BD9', fontWeight: '700' }}
                        onPress={() => navigation.navigate('AccountEdit')}
                      >
                        settings
                      </Text>{' '}
                      page, to edit your details.
                    </Text>
                  </View>
                )}

                {/* Title */}
                <View style={{ marginBottom: 16 }}>
                  <DropDownPicker
                    disabled={isDisabled}
                    items={STRINGS.OPENFLOW_TITLES.map((ITEM) => ({ value: ITEM, label: ITEM }))}
                    title={'Title'}
                    defaultValue={isDesignatedLandlord === 'No' ? desLandlord?.title : state?.title}
                    onChangeItem={(e) => _handleChange({ value: e.value, label: 'title' })}
                    error={titleError ? true : false}
                  />
                </View>

                {/* First Name + Last Name row */}
                <View style={ws.inputRow}>
                  <View style={ws.inputHalf}>
                    <FInput
                      label="First Name"
                      required
                      value={fieldVal('first_name', 'first_name')}
                      onChangeText={fieldChange('first_name')}
                      editable={!isDisabled}
                      error={firstNameError}
                    />
                  </View>
                  <View style={ws.inputHalf}>
                    <FInput
                      label="Last Name"
                      required
                      value={fieldVal('last_name', 'last_name')}
                      onChangeText={fieldChange('last_name')}
                      editable={!isDisabled}
                      error={lastError}
                    />
                  </View>
                </View>

                {/* Current Address */}
                <FInput
                  label="Current Address"
                  placeholder="Enter your full address"
                  value={fieldVal('address', 'address')}
                  onChangeText={fieldChange('address')}
                  editable={!isDisabled}
                  error={addressError}
                />

                {/* Postcode */}
                <FInput
                  label="Postcode"
                  value={fieldVal('postcode', 'postcode')}
                  onChangeText={fieldChange('postcode')}
                  editable={!isDisabled}
                  error={pinCodeError}
                />

                {/* CONTACTS section */}
                <Text style={ws.contactsLabel}>CONTACTS</Text>

                {/* Contact Number + Email row */}
                <View style={ws.inputRow}>
                  <View style={ws.inputHalf}>
                    <FInput
                      label="Contact Number"
                      required
                      placeholder="e.g. +44 20 7123 4567"
                      value={fieldVal('contact_number', 'contact_number')}
                      onChangeText={(val) => {
                        const numericValue = val.replace(/[^0-9]/g, '');
                        _handleChange({ value: numericValue, label: 'contact_number' });
                      }}
                      editable={!isDisabled}
                      keyboardType="phone-pad"
                    />
                  </View>
                  <View style={ws.inputHalf}>
                    <FInput
                      label="Email Address"
                      required
                      placeholder="hubita@gmail.com"
                      value={fieldVal('email', 'email')}
                      onChangeText={fieldChange('email')}
                      editable={!isDisabled}
                      error={emailError}
                      keyboardType="email-address"
                    />
                  </View>
                </View>
              </>
            ) : null}
          </View>

          {/* ── Sidebar ── */}
          <SetupSidebar
            activeKey="landlord"
            propertyId={activePropertyId}
            propertyName={propertyDisplayName}
            navigation={navigation}
          />
        </View>
      </ScrollView>

      {/* Sticky footer */}
      <PropertyStickyFooter
        onSave={() => (isDesignatedLandlord === 'No' ? _validation() : _onSave())}
        isMobile={isMobile}
      />
    </View>
  );
};

/* ═══════════════════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════════════════ */

/* ── MOBILE ── */
const ms = StyleSheet.create({
  root: { flex: 1, backgroundColor: '#F5F7FB' },
  scroll: { padding: 16, paddingBottom: 30 },

  /* Progress card */
  progressCard: {
    backgroundColor: '#fff',
    borderRadius: 14,
    padding: 14,
    marginBottom: 12,
    ...SHADOWS.sm,
  },
  progressTop: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  progressDot: {
    width: 10, height: 10, borderRadius: 5,
    backgroundColor: '#6C2BD9', flexShrink: 0,
  },
  progressTitle: { fontSize: 14, fontWeight: '700', color: '#111827' },
  progressSub: { fontSize: 11, color: '#9CA3AF', marginTop: 1 },
  viewAllBtn: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  viewAllText: { fontSize: 12, color: '#6C2BD9', fontWeight: '600' },

  /* Header card */
  headerCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    backgroundColor: '#fff',
    borderRadius: 14,
    padding: 16,
    marginBottom: 12,
    ...SHADOWS.sm,
  },
  headerIcon: {
    width: 40, height: 40, borderRadius: 10,
    backgroundColor: '#F0E8FF',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  headerTitle: { fontSize: 17, fontWeight: '700', color: '#111827' },
  headerSub: { fontSize: 12, color: '#9CA3AF', marginTop: 3, lineHeight: 17 },

  /* Complete card */
  completeCard: {
    backgroundColor: '#fff',
    borderRadius: 14,
    paddingHorizontal: 16,
    paddingVertical: 4,
    marginBottom: 12,
    ...SHADOWS.sm,
  },

  /* Section cards */
  sectionCard: {
    backgroundColor: '#fff',
    borderRadius: 14,
    padding: 16,
    marginBottom: 12,
    ...SHADOWS.sm,
  },
  sectionTitle: { fontSize: 15, fontWeight: '700', color: '#111827' },
  sectionSub: { fontSize: 13, color: '#9CA3AF', marginTop: 3 },

  /* Info note */
  infoNote: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    backgroundColor: '#F5EEFF',
    borderRadius: 10,
    padding: 12,
    marginBottom: 16,
  },
  infoNoteText: { fontSize: 13, color: '#6B7280', flex: 1, lineHeight: 19 },

  contactsLabel: {
    fontSize: 11, fontWeight: '700', color: '#9CA3AF',
    letterSpacing: 0.8, textTransform: 'uppercase',
    marginBottom: 12, marginTop: 8,
  },

  /* Sticky footer */
  footer: {
    backgroundColor: '#fff',
    borderTopWidth: 1, borderTopColor: '#E5E7EB',
    padding: 16,
    ...SHADOWS.md,
  },
  footerBtn: {
    backgroundColor: '#6C2BD9',
    borderRadius: 30,
    paddingVertical: 14,
    alignItems: 'center',
  },
  footerBtnText: { color: '#fff', fontSize: 15, fontWeight: '700' },
});

/* ── WEB / TABLET ── */
const ws = StyleSheet.create({
  root: { flex: 1, backgroundColor: '#F5F7FB' },

  /* Breadcrumb */
  breadcrumbBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 24,
    paddingVertical: 14,
    flexWrap: 'wrap',
  },
  backBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, marginRight: 4 },
  backText: { fontSize: 13, color: '#6C2BD9', fontWeight: '600' },
  /* Banner Card */
  bannerCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    gap: 12,
    ...SHADOWS.sm,
  },
  bannerCardMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  bannerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    width: '100%',
  },
  bannerIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#F5F3FF',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  bannerTextWrap: {
    flex: 1,
    minWidth: 0,
  },
  bannerTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111827',
  },
  bannerSubtitle: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  bannerRight: {},

  /* Body layout */
  body: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 20,
    paddingHorizontal: 24,
    paddingBottom: 100,
  },

  /* Main card */
  mainCard: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 24,
    ...SHADOWS.md,
  },

  /* Card header */
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    marginBottom: 24,
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
    flexWrap: 'wrap',
  },
  cardIconWrap: {
    width: 44, height: 44, borderRadius: 12,
    backgroundColor: '#F0E8FF',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  cardTitle: { fontSize: 20, fontWeight: '700', color: '#111827' },
  cardSub: { fontSize: 13, color: '#9CA3AF', marginTop: 3 },

  /* "Are you the landlord?" row */
  questionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 24,
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  questionTitle: { fontSize: 15, fontWeight: '700', color: '#111827' },
  questionSub: { fontSize: 13, color: '#9CA3AF', marginTop: 3 },

  /* Info note */
  infoNote: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    backgroundColor: '#F5EEFF',
    borderRadius: 10,
    padding: 14,
    marginBottom: 20,
  },
  infoNoteText: { fontSize: 13, color: '#6B7280', flex: 1, lineHeight: 20 },

  /* Two-column input row */
  inputRow: { flexDirection: 'row', gap: 16, marginBottom: 0 },
  inputHalf: { flex: 1 },

  /* CONTACTS label */
  contactsLabel: {
    fontSize: 11, fontWeight: '700', color: '#9CA3AF',
    letterSpacing: 0.8, textTransform: 'uppercase',
    marginBottom: 12, marginTop: 4,
  },

  /* Sticky footer */
  stickyFooter: {
    position: 'absolute',
    bottom: 0, left: 0, right: 0,
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 14,
    zIndex: 1000,
    ...SHADOWS.md,
  },
  footerNote: { fontSize: 13, color: '#9CA3AF' },
  footerBtn: {
    backgroundColor: '#6C2BD9',
    borderRadius: 30,
    paddingHorizontal: 24,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  footerBtnText: { color: '#fff', fontSize: 14, fontWeight: '700' },
});

export default drawerHoc({
  Component: LandlordDetails,
  showBackButton: true,
  noScroll: true,
});
